import { Component } from '@theme/component';
import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';

const SCROLL_STORAGE_KEY = 'av-load-more-scroll';

/**
 * A "Load more" button for a collection grid. Fetches the next page via
 * the section rendering API and appends the new cards, keeping the real
 * `?page=` URL crawlable (via the sibling <noscript> pagination) and
 * restoring scroll position when the shopper navigates back.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [button]
 *
 * @extends {Component<Refs>}
 */
class AvLoadMore extends Component {
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', this.#restoreScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#restoreScroll);
  }

  loadNextPage = async () => {
    const nextUrl = this.getAttribute('next-url');
    if (!nextUrl) return;

    const { button } = this.refs;
    if (button) button.disabled = true;

    try {
      const sectionId = this.getAttribute('section-id') ?? '';
      const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(nextUrl, window.location.href));
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const currentGrid = this.closest('.shopify-section')?.querySelector('[ref="grid"]');
      const newGrid = parsed.getElementById(buildSectionSelector(sectionId))?.querySelector('[ref="grid"]');

      if (currentGrid && newGrid) {
        currentGrid.append(...newGrid.querySelectorAll(':scope > [ref="cards[]"]'));
      }

      const newLoadMore = parsed.querySelector('av-load-more');
      const newNextUrl = newLoadMore?.getAttribute('next-url');

      if (newNextUrl) {
        this.setAttribute('next-url', newNextUrl);
      } else {
        this.hidden = true;
      }

      try {
        sessionStorage.setItem(
          `${SCROLL_STORAGE_KEY}:${window.location.pathname}`,
          JSON.stringify({ scrollY: window.scrollY })
        );
      } catch {
        // Storage unavailable; scroll restoration is a nice-to-have.
      }

      history.pushState({ avLoadMore: true, scrollY: window.scrollY }, '', nextUrl);
    } finally {
      if (button) button.disabled = false;
    }
  };

  #restoreScroll = () => {
    try {
      const raw = sessionStorage.getItem(`${SCROLL_STORAGE_KEY}:${window.location.pathname}`);
      if (!raw) return;
      const { scrollY } = JSON.parse(raw);
      if (typeof scrollY === 'number') window.scrollTo({ top: scrollY, behavior: 'instant' });
    } catch {
      // No-op: scroll restoration is best-effort.
    }
  };
}

if (!customElements.get('av-load-more')) {
  customElements.define('av-load-more', AvLoadMore);
}
